import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Title } from '@angular/platform-browser';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AppModuleCode } from '../models/tenant.model';
import { ADMIN_CONTEXT, AuthService } from '../services/auth.service';
import { CartService } from '../services/cart.service';
import { StoreService } from '../services/store.service';
import { SuperAdminService } from '../services/super-admin.service';
import { TenantService } from '../services/tenant.service';

// Páginas da plataforma (landing, cadastro, login do administrador): valem a sessão do painel
export const platformGuard: CanActivateFn = () => {
  inject(AuthService).setContext(ADMIN_CONTEXT);
  inject(Title).setTitle(environment.platformName);
  return true;
};

// Vitrine de uma empresa (/:slug): carrega a loja e passa a usar a sessão e o carrinho dela.
// Endereço que não é de nenhuma loja volta para a página inicial.
export const storeGuard: CanActivateFn = (route) => {
  const authService = inject(AuthService);
  const cartService = inject(CartService);
  const router = inject(Router);
  const title = inject(Title);
  const slug = (route.paramMap.get('slug') ?? '').toLowerCase();

  return inject(StoreService).load(slug).pipe(
    map((store) => {
      title.setTitle(store.name);
      authService.setContext(slug);
      cartService.useStore(slug);
      return true;
    }),
    catchError(() => of(router.createUrlTree(['/'])))
  );
};

// Telas da loja que exigem o cliente logado (usar nas rotas filhas de /:slug)
export const customerGuard: CanActivateFn = (_route, state) => {
  const router = inject(Router);
  if (inject(AuthService).isAuthenticated()) return true;

  // Guarda a intenção de retornar à rota pedida após o login
  localStorage.setItem('redirect_after_login', state.url);
  return router.createUrlTree(inject(StoreService).link('login'));
};

// Tela da loja que depende de um módulo do plano da empresa: sem ele, volta ao cardápio
export function storeModuleGuard(module: AppModuleCode): CanActivateFn {
  return () => {
    const store = inject(StoreService);
    return store.hasModule(module) ? true : inject(Router).createUrlTree(store.link());
  };
}

// Painel: só o administrador, com a empresa dele carregada (assinatura e módulos)
export const adminGuard: CanActivateFn = (_route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  authService.setContext(ADMIN_CONTEXT);
  inject(Title).setTitle(`Painel · ${environment.platformName}`);

  if (!authService.isAdmin()) {
    localStorage.setItem('redirect_after_login', state.url);
    return router.createUrlTree(['/entrar']);
  }

  return inject(TenantService).load().pipe(
    map(() => true),
    // Sessão inválida (401: o interceptor já a encerrou) volta ao login. Outras falhas (API fora do ar)
    // deixam entrar: as telas mostram o próprio erro, e mandar para o login criaria um vai-e-vem sem fim.
    catchError((err: HttpErrorResponse) => of(err.status === 401 ? router.createUrlTree(['/entrar']) : true))
  );
};

// Telas do painel que param de funcionar com o trial/assinatura vencidos (ou sem o módulo no plano):
// o administrador é levado à tela de assinatura. Usar nas rotas filhas de /admin.
export function adminAccessGuard(module?: AppModuleCode): CanActivateFn {
  return () => {
    const tenantService = inject(TenantService);
    const router = inject(Router);
    const overview = tenantService.overview();

    if (!overview?.subscription.active) return router.createUrlTree(['/admin', 'subscription']);
    if (module && !tenantService.hasModule(module)) return router.createUrlTree(['/admin', 'subscription']);
    return true;
  };
}

// Portal do administrador geral (/super-admin): sessão própria, separada do painel e das lojas
export const superAdminGuard: CanActivateFn = () => {
  inject(Title).setTitle(`Administração geral · ${environment.platformName}`);
  return inject(SuperAdminService).isAuthenticated() ? true : inject(Router).createUrlTree(['/super-admin', 'login']);
};

// Tela de login do portal: quem já entrou vai direto para a visão geral
export const superAdminLoginGuard: CanActivateFn = () => {
  inject(Title).setTitle(`Administração geral · ${environment.platformName}`);
  return inject(SuperAdminService).isAuthenticated() ? inject(Router).createUrlTree(['/super-admin']) : true;
};
